import { useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { useCourse, usePublishCourse, useArchiveCourse } from "@/features/courses/hooks/useCourses"
import { useStartSession } from "@/features/sessions/hooks/useSessions"
import { CourseStatusBadge } from "@/features/courses/components/CourseStatusBadge"
import { CreateCourseModal } from "@/features/courses/components/CreateCourseModal"
import { DocumentUploadZone } from "@/features/courses/components/DocumentUploadZone"
import { Button } from "@/components/ui/Button"
import { Alert } from "@/components/ui/Alert"
import { useTranslation } from 'react-i18next'

const LANGUAGE_LABELS: Record<string, string> = {
  fr: "Français",
  en: "English",
}

const MODE_LABELS: Record<string, string> = {
  free: "Cours libre",
  rag: "RAG",
  hybrid: "Hybride",
}

function DetailSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="flex items-center gap-2">
        <div className="h-4 w-20 rounded bg-surface-100" />
        <div className="h-4 w-4 rounded bg-surface-100" />
        <div className="h-4 w-40 rounded bg-surface-100" />
      </div>
      <div className="rounded-2xl border border-surface-200 bg-white p-6">
        <div className="space-y-3">
          <div className="h-7 w-2/3 rounded bg-surface-100" />
          <div className="h-4 w-1/3 rounded bg-surface-100" />
          <div className="mt-4 h-4 w-full rounded bg-surface-100" />
          <div className="h-4 w-4/5 rounded bg-surface-100" />
        </div>
      </div>
    </div>
  )
}

export function CourseDetailPage() {
  const { t } = useTranslation()
  const { id = "" } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [editModalOpen, setEditModalOpen] = useState(false)

  const { data: course, isLoading, isError } = useCourse(id)
  const publishMutation = usePublishCourse()
  const archiveMutation = useArchiveCourse()
  const startSession = useStartSession()

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <DetailSkeleton />
      </div>
    )
  }

  if (isError || !course) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <Alert variant="error">
          Ce cours est introuvable ou une erreur est survenue.{" "}
          <button
            type="button"
            onClick={() => navigate("/courses")}
            className="underline hover:no-underline"
          >
            {t('courses.back_to_courses')}
          </button>
        </Alert>
      </div>
    )
  }

  const showDocuments = course.mode === "rag" || course.mode === "hybrid"
  const canStartSession = course.status === "active"

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      {/* Breadcrumb */}
      <nav aria-label={t('courses.breadcrumb')} className="mb-6">
        <ol className="flex items-center gap-1.5 text-sm text-neutral-600">
          <li>
            <Link
              to="/courses"
              className="transition-colors hover:text-mindeia-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-mindeia-primary"
            >
              {t('courses.title')}
            </Link>
          </li>
          <li aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M4.5 2.5L8 6l-3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </li>
          <li className="truncate font-medium text-neutral-900" aria-current="page">
            {course.title}
          </li>
        </ol>
      </nav>

      {/* Course card */}
      <div className="rounded-2xl border border-surface-200 bg-white p-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-neutral-900 break-words">{course.title}</h1>
              <CourseStatusBadge status={course.status} />
            </div>
            <p className="mt-1 text-sm font-medium text-neutral-600">{course.subject}</p>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEditModalOpen(true)}
            >
              {t('courses.edit')}
            </Button>

            {course.status === "draft" && (
              <Button
                size="sm"
                isLoading={publishMutation.isPending}
                onClick={() => publishMutation.mutate(course.id)}
              >
                {t('courses.publish')}
              </Button>
            )}

            {course.status === "active" && (
              <Button
                variant="secondary"
                size="sm"
                isLoading={archiveMutation.isPending}
                onClick={() => archiveMutation.mutate(course.id)}
              >
                {t('courses.archive')}
              </Button>
            )}
          </div>
        </div>

        {/* Description */}
        {course.description && (
          <p className="mt-4 text-sm leading-relaxed text-neutral-700">{course.description}</p>
        )}

        {/* Meta grid */}
        <dl className="mt-5 grid grid-cols-2 gap-4 rounded-xl bg-surface-50 p-4 sm:grid-cols-3">
          <div>
            <dt className="text-xs text-neutral-600">{t('courses.detail_mode')}</dt>
            <dd className="mt-0.5 text-sm font-semibold text-neutral-900">
              {MODE_LABELS[course.mode] ?? course.mode}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-neutral-600">{t('courses.detail_language')}</dt>
            <dd className="mt-0.5 text-sm font-semibold text-neutral-900">
              {LANGUAGE_LABELS[course.language] ?? course.language}
            </dd>
          </div>
          {course.documentsCount !== undefined && showDocuments && (
            <div>
              <dt className="text-xs text-neutral-600">{t('courses.detail_documents')}</dt>
              <dd className="mt-0.5 text-sm font-semibold text-neutral-900">
                {course.documentsCount} doc{course.documentsCount !== 1 ? "s" : ""}
              </dd>
            </div>
          )}
        </dl>

        {/* Session button */}
        <div className="mt-6">
          {canStartSession ? (
            <Button
              className="w-full sm:w-auto"
              isLoading={startSession.isPending}
              onClick={() => startSession.mutate(course.id)}
            >
              {t('courses.start_session')}
            </Button>
          ) : (
            <div>
              <button
                type="button"
                disabled
                className="rounded-lg bg-surface-100 px-5 py-2.5 text-sm font-semibold text-neutral-600 cursor-not-allowed opacity-60"
                style={{ minHeight: "44px" }}
              >
                {t('courses.start_session')}
              </button>
              <p className="mt-2 text-xs text-neutral-600">
                {course.status === "draft"
                  ? "Publiez le cours pour démarrer une session."
                  : "Ce cours est archivé."}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Documents section */}
      {showDocuments && (
        <section className="mt-6 rounded-2xl border border-surface-200 bg-white p-6">
          <h2 className="mb-4 text-base font-semibold text-neutral-900">
            {t('courses.documents_section')}
          </h2>
          <DocumentUploadZone courseId={course.id} documents={[]} />
        </section>
      )}

      {/* Edit modal */}
      {editModalOpen && (
        <CreateCourseModal
          open
          onClose={() => setEditModalOpen(false)}
          editCourse={course}
        />
      )}
    </div>
  )
}
